import { useState, useMemo } from "react";
import {
  Box,
  Container,
  Paper,
  Grid,
  Typography,
  TextField,
} from "@mui/material";
import {
  startOfToday,
  startOfWeek,
  endOfWeek,
  isWithinInterval,
  parseISO,
  isValid,
} from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import Page from "./Page";
import useRepCalls from "../../hooks/useRepCalls";
import { useSelector } from "react-redux";
import LocationsList from "../elements/LocationsList";
import LocationsMap from "../elements/LocationsMap";
import { getId } from "../../utils/idHelpers";
import useLocalization from "../../hooks/useLocalization";

const ScheduledLocations = () => {
  const currentUserId = useSelector((state) => state.authentication.userId);
  const { calls } = useRepCalls(currentUserId);
  const { getLabel } = useLocalization();
  const [expandedLocationId, setExpandedLocationId] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [hoveredLocationId, setHoveredLocationId] = useState(null);

  // Default: current week (start of week through end of week)
  const defaultWeekStart = startOfWeek(startOfToday());
  const defaultWeekEnd = endOfWeek(defaultWeekStart);
  const [filterStart, setFilterStart] = useState(defaultWeekStart);
  const [filterEnd, setFilterEnd] = useState(defaultWeekEnd);
  const [searchText, setSearchText] = useState("");

  const formatAddress = (address) => {
    if (!address) return "";
    if (typeof address === "string") return address;

    const parts = [
      address.streetAddress,
      address.city,
      address.province,
      address.postal,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const getCoordinateValue = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getCoordinatesFromAddress = (address) => {
    if (!address || typeof address !== "object") {
      return { latitude: null, longitude: null };
    }

    const coordinateArray =
      address.coordinates || address.coords || address.geoCoordinates;
    if (Array.isArray(coordinateArray) && coordinateArray.length >= 2) {
      const [lng, lat] = coordinateArray;
      return {
        latitude: getCoordinateValue(lat),
        longitude: getCoordinateValue(lng),
      };
    }

    const latitude =
      address.latitude ||
      address.lat ||
      address.geo?.latitude ||
      address.geo?.lat ||
      address.location?.latitude ||
      address.location?.lat ||
      address.coordinates?.latitude ||
      address.coordinates?.lat;
    const longitude =
      address.longitude ||
      address.lng ||
      address.lon ||
      address.geo?.longitude ||
      address.geo?.lng ||
      address.geo?.lon ||
      address.location?.longitude ||
      address.location?.lng ||
      address.location?.lon ||
      address.coordinates?.longitude ||
      address.coordinates?.lng ||
      address.coordinates?.lon;

    return {
      latitude: getCoordinateValue(latitude),
      longitude: getCoordinateValue(longitude),
    };
  };

  const getCallDate = (call) => {
    const rawDate = call.scheduledDate || call.scheduleDate || call.yyyymmdd;
    if (!rawDate) return null;

    const isoDate = typeof rawDate === "string" ? parseISO(rawDate) : rawDate;
    if (isValid(isoDate)) return isoDate;

    const fallbackDate = new Date(rawDate);
    return isValid(fallbackDate) ? fallbackDate : null;
  };

  // Filter calls for current week and group by location
  const locationMap = useMemo(() => {
    const grouped = {};
    const trimmedSearch = searchText.trim().toLowerCase();
    const rangeStart = filterStart || defaultWeekStart;
    const rangeEnd = filterEnd || defaultWeekEnd;
    const intervalStart = rangeStart <= rangeEnd ? rangeStart : rangeEnd;
    const intervalEnd = rangeStart <= rangeEnd ? rangeEnd : rangeStart;

    calls.forEach((call) => {
      const callDate = getCallDate(call);
      if (!callDate) {
        return;
      }

      if (
        isWithinInterval(callDate, { start: intervalStart, end: intervalEnd })
      ) {
        const locationId = getId(call.location) || call.locationId;
        if (!locationId) {
          return;
        }

        const locationName = call.location?.name || call.locationName;
        const locationNumber =
          call.location?.locationNumber || call.locationNumber;
        const rawAddress = call.location?.address || call.locationAddress;
        const locationAddress = formatAddress(rawAddress);
        const addressCoordinates = getCoordinatesFromAddress(rawAddress);
        const rawLatitude =
          call.location?.latitude ||
          call.location?.lat ||
          addressCoordinates.latitude;
        const rawLongitude =
          call.location?.longitude ||
          call.location?.lng ||
          call.location?.lon ||
          addressCoordinates.longitude;
        const locationText = `${locationName || ""} ${locationNumber || ""} ${
          locationAddress || ""
        }`.toLowerCase();
        const projectName = call.project?.name || call.projectName || "";
        const matchesSearch =
          !trimmedSearch ||
          locationText.includes(trimmedSearch) ||
          projectName.toLowerCase().includes(trimmedSearch);

        if (!matchesSearch) {
          return;
        }

        if (!grouped[locationId]) {
          grouped[locationId] = {
            id: locationId,
            name: locationName,
            number: locationNumber,
            address: locationAddress,
            latitude: getCoordinateValue(rawLatitude),
            longitude: getCoordinateValue(rawLongitude),
            calls: [],
          };
        }

        grouped[locationId].calls.push({
          id: getId(call) || call.id,
          scheduleDate:
            call.scheduledDate || call.scheduleDate || call.yyyymmdd,
          projectName,
          status: call.status,
        });
      }
    });

    return Object.values(grouped).sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
  }, [
    calls,
    filterStart,
    filterEnd,
    searchText,
    defaultWeekStart,
    defaultWeekEnd,
  ]);

  return (
    <Page pageName="scheduledLocations">
      <Container
        maxWidth={false}
        disableGutters
        sx={{ py: 2, px: 2, height: "100%" }}
      >
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid
            container
            spacing={2}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              md={4}
            >
              <Typography variant="subtitle2">{getLabel("filters")}</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={getLabel("startDate")}
                  value={filterStart}
                  onChange={setFilterStart}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={getLabel("endDate")}
                  value={filterEnd}
                  onChange={setFilterEnd}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
            >
              <TextField
                label={getLabel("searchLocationOrProject")}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            height: "calc(100vh - 260px)",
          }}
        >
          <Box sx={{ width: "40%", minWidth: 320 }}>
            <Paper sx={{ p: 2, height: "100%", overflow: "auto" }}>
              <LocationsList
                locations={locationMap}
                expandedLocationId={expandedLocationId}
                onExpandToggle={setExpandedLocationId}
                onSelectLocation={setSelectedLocationId}
                selectedLocationId={selectedLocationId}
                onHoverLocation={setHoveredLocationId}
              />
            </Paper>
          </Box>

          <Box sx={{ width: "60%", minWidth: 480 }}>
            <Paper
              sx={{
                p: 2,
                height: "100%",
                minHeight: 600,
                overflow: "hidden",
              }}
            >
              <LocationsMap
                locations={locationMap}
                selectedLocationId={selectedLocationId}
                hoveredLocationId={hoveredLocationId}
                onSelectLocation={setSelectedLocationId}
              />
            </Paper>
          </Box>
        </Box>
      </Container>
    </Page>
  );
};

export default ScheduledLocations;
